import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, FINANCE_NAME } from '../../config';
import './FinanceMeeting.css';

const FinanceMeeting = () => {
  const [date, setDate] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [outstandingLoan, setOutstandingLoan] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getOutstandingAmount = (row) => toNumber(row.outstanding_balance ?? row.remaining_amount);

  const formatCurrency = (value) => {
    const amount = toNumber(value);
    return `₹${amount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  useEffect(() => {
    setDate(new Date().toDateString());
  }, []);

  useEffect(() => {
    const fetchFinanceMeetingData = async () => {
      setLoading(true);
      setError('');

      try {
        const [balanceRes, partnersRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/secondary-balance-enquiry`, { timeout: 60000 }),
          axios.get(`${API_BASE_URL}/partners`, { timeout: 60000 }),
        ]);

        setBalanceData(balanceRes.data || null);

        const partners = Array.isArray(partnersRes.data)
          ? partnersRes.data.map((p) => (typeof p === 'string' ? p.trim() : '')).filter(Boolean)
          : [];

        if (!partners.length) {
          setOutstandingLoan(0);
          return;
        }

        const loanSummaries = await Promise.all(
          partners.map(async (partner) => {
            const partnerEnc = encodeURIComponent(partner);
            const [glDueR, cdDueR, tbdDueR] = await Promise.allSettled([
              axios.get(
                `${API_BASE_URL}/api/general-loan-partnerwise-due-report?partner=${partnerEnc}`,
                { timeout: 60000 }
              ),
              axios.get(
                `${API_BASE_URL}/api/cd-loan-partnerwise-due-report?partner=${partnerEnc}`,
                { timeout: 60000 }
              ),
              axios.get(
                `${API_BASE_URL}/api/tbd-loan-partnerwise-due-report?partner=${partnerEnc}`,
                { timeout: 60000 }
              ),
            ]);

            const glDue =
              glDueR.status === 'fulfilled' && Array.isArray(glDueR.value.data)
                ? glDueR.value.data
                : [];
            const cdDue =
              cdDueR.status === 'fulfilled' && Array.isArray(cdDueR.value.data)
                ? cdDueR.value.data
                : [];
            const tbdDue =
              tbdDueR.status === 'fulfilled' && Array.isArray(tbdDueR.value.data)
                ? tbdDueR.value.data
                : [];

            const generalLoan = glDue.reduce((sum, row) => sum + getOutstandingAmount(row), 0);
            const cdLoan = cdDue.reduce((sum, row) => sum + getOutstandingAmount(row), 0);
            const tbdLoan = tbdDue.reduce((sum, row) => sum + getOutstandingAmount(row), 0);

            return generalLoan + cdLoan + tbdLoan;
          })
        );

        setOutstandingLoan(loanSummaries.reduce((sum, value) => sum + toNumber(value), 0));
      } catch (err) {
        console.error('Error fetching finance meeting data:', err);
        const status = err.response?.status;
        const apiError = err.response?.data?.error || err.response?.data?.message;
        setError(
          `Failed to load finance meeting data${status ? ` (HTTP ${status})` : ''}${
            apiError ? `: ${apiError}` : err.message ? `: ${err.message}` : ''
          }`
        );
        setBalanceData(null);
        setOutstandingLoan(0);
      } finally {
        setLoading(false);
      }
    };

    fetchFinanceMeetingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const creditRows = useMemo(() => {
    if (!balanceData) return [];

    const partnerTransactions = toNumber(balanceData.secondaryPartnerCredits);
    const totalInterestAndOd =
      toNumber(balanceData.secondaryGeneralLoanInterest) +
      toNumber(balanceData.secondaryCdLoanInterest) +
      toNumber(balanceData.secondaryTbdLoanInterest) +
      toNumber(balanceData.secondaryGlOverdue) +
      toNumber(balanceData.secondaryCdOverdue) +
      toNumber(balanceData.secondaryTbdOverdue);
    const documentCharges = toNumber(balanceData.secondaryDocumentChargesCredit);
    const rdCredit = toNumber(balanceData.secondaryRdCredit);

    return [
      { label: 'Partner Transactions', value: partnerTransactions },
      { label: 'Total Interest and OD', value: totalInterestAndOd },
      { label: 'Document Charges', value: documentCharges },
      { label: 'RD Credit', value: rdCredit },
    ];
  }, [balanceData]);

  const debitRows = useMemo(() => {
    if (!balanceData) return [];

    return [
      { label: 'Outstanding Loan', value: toNumber(outstandingLoan) },
      { label: 'Lucky Draw', value: toNumber(balanceData.secondaryRdDebit) },
      { label: 'Expense', value: toNumber(balanceData.secondaryExpenseDebit) },
    ];
  }, [balanceData, outstandingLoan]);

  const creditTotal = useMemo(
    () => creditRows.reduce((sum, row) => sum + toNumber(row.value), 0),
    [creditRows]
  );

  const debitTotal = useMemo(
    () => debitRows.reduce((sum, row) => sum + toNumber(row.value), 0),
    [debitRows]
  );

  const handlePrint = () => {
    const printContent = document.getElementById('finance-meeting-print-area')?.innerHTML;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Finance Meeting - ${FINANCE_NAME}</title>
          <style>
            @page { size: A4; margin: 12mm; }
            body {
              font-family: Arial, sans-serif;
              padding: 0;
              margin: 0;
              color: #222;
            }
            h1 {
              text-align: center;
              font-size: 22px;
              margin: 0 0 6px;
              text-transform: uppercase;
            }
            h2 {
              text-align: center;
              font-size: 18px;
              margin: 0 0 6px;
            }
            .print-date {
              text-align: center;
              font-size: 14px;
              margin: 0 0 18px;
              color: #333;
            }
            .finance-meeting-tables {
              display: block;
            }
            .finance-meeting-table-card {
              margin-bottom: 18px;
              page-break-inside: avoid;
            }
            .finance-meeting-table-card h3 {
              text-align: center;
              font-size: 16px;
              margin: 0 0 8px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            th, td {
              border: 1px solid #ccc;
              padding: 8px;
            }
            th {
              background-color: #0476df;
              color: #fff;
              font-weight: bold;
              text-align: left;
            }
            td:last-child, th:last-child {
              text-align: right;
            }
            .finance-meeting-total-row td {
              font-weight: bold;
              background: #e8f4ff;
            }
            .credit-amount { color: #1b7a3d; }
            .debit-amount { color: #b00020; }
          </style>
        </head>
        <body>
          <h1>${FINANCE_NAME}</h1>
          <h2>Finance Meeting</h2>
          <p class="print-date">Date: ${date || new Date().toDateString()}</p>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="finance-meeting-container">
      <h2 className="heading">Finance Meeting</h2>
      <p className="date">Date: {date}</p>

      {loading ? (
        <div className="finance-meeting-loading">Loading finance meeting data...</div>
      ) : error ? (
        <div className="finance-meeting-error">{error}</div>
      ) : (
        <>
          <div id="finance-meeting-print-area" className="finance-meeting-tables">
            <div className="finance-meeting-table-card">
              <h3>Credits</h3>
              <table className="finance-meeting-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {creditRows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td className="credit-amount">{formatCurrency(row.value)}</td>
                    </tr>
                  ))}
                  <tr className="finance-meeting-total-row">
                    <td>Total</td>
                    <td className="credit-amount">{formatCurrency(creditTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="finance-meeting-table-card">
              <h3>Debits</h3>
              <table className="finance-meeting-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {debitRows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td className="debit-amount">{formatCurrency(row.value)}</td>
                    </tr>
                  ))}
                  <tr className="finance-meeting-total-row">
                    <td>Total</td>
                    <td className="debit-amount">{formatCurrency(debitTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <button
            type="button"
            onClick={handlePrint}
            className="print-button"
            disabled={!balanceData}
          >
            Print Tables
          </button>
        </>
      )}
    </div>
  );
};

export default FinanceMeeting;
